import { useState } from "react"
import type { ReactNode } from "react"
import Button from "@/components/Button/Button"
import Modal from "@/components/Modal/Modal"
import TextField from "@/components/TextField/TextField"
import { BinGlyph, BoxGlyph } from "@/components/RemoveDialog/removeGlyphs"
import { REMOVE_ACTION, REMOVE_CONFIRM_LABEL, REMOVE_PENDING } from "@/content/archive"
import type { RemovalKind } from "@/types"
import styles from "@/styles/components/RemoveDialog.module.css"

/**
 * The confirmation in front of an archive or a delete. Same dialog either way;
 * the copy, the glyph and the typed-name bar are what separate the two intents.
 */

interface RemoveDialogProps {
  kind: RemovalKind
  title: string
  description: string
  /**
   * What is about to travel with it — a session count, a room that is running.
   * Rendered under the blurb, and it is the line people actually read.
   */
  detail?: ReactNode
  warning?: ReactNode
  /**
   * Demands this exact string be typed before the button unlocks. Passed for
   * deleting a customer and nothing else — see `REMOVE_CONFIRM_LABEL`.
   */
  confirmWith?: string
  error: string | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}

export default function RemoveDialog({
  kind,
  title,
  description,
  detail,
  warning,
  confirmWith,
  error,
  pending,
  onClose,
  onConfirm,
}: RemoveDialogProps) {
  const [typed, setTyped] = useState("")
  const unlocked = confirmWith === undefined || typed.trim() === confirmWith.trim()

  return (
    <Modal
      title={title}
      description={description}
      icon={kind === "deleted" ? <BinGlyph /> : <BoxGlyph />}
      width="narrow"
      dismissible
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className={kind === "deleted" ? styles.confirm : undefined}
            disabled={!unlocked}
            loading={pending}
            onClick={onConfirm}
          >
            {pending ? REMOVE_PENDING[kind] : REMOVE_ACTION[kind]}
          </Button>
        </>
      }
    >
      {detail !== undefined ? <p className={styles.detail}>{detail}</p> : null}

      {warning !== undefined ? (
        <p
          className={
            kind === "deleted" ? `${styles.warning} ${styles.warningFinal}` : styles.warning
          }
        >
          {warning}
        </p>
      ) : null}

      {confirmWith !== undefined ? (
        <TextField
          label={REMOVE_CONFIRM_LABEL}
          placeholder={confirmWith}
          autoFocus
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      ) : null}

      {error !== null ? <p className={styles.error}>{error}</p> : null}
    </Modal>
  )
}
